import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Mail } from 'lucide-react';
import footerService, { FooterSection } from '../services/footerService';
import TopCitiesSection from './places/TopCitiesSection';
import { t } from '../i18n';

// ============================================
// Footer – Port des Flutter AppFooter.
// Lädt Sektionen dynamisch (GET /api/footer/sections), mit Fallback-Sektionen
// falls das Backend nicht erreichbar ist. Responsive (Desktop/Mobile).
// ============================================

const FALLBACK_SECTIONS: FooterSection[] = [
  {
    id: '1',
    title: t('web.component.footer.entdecken'),
    order: 0,
    isVisible: true,
    items: [
      { id: '1', label: t('web.component.footer.turniere'), route: '/turniere', order: 0, isVisible: true },
      { id: '2', label: t('web.component.footer.vereine'), route: '/vereine', order: 1, isVisible: true },
      { id: '3', label: t('web.component.footer.spielstaetten'), route: '/spielstaetten', order: 2, isVisible: true },
      { id: '4', label: t('web.component.footer.shops'), route: '/shops', order: 3, isVisible: true },
      { id: '5', label: t('web.component.footer.turnierserien'), route: '/turnierserien', order: 4, isVisible: true },
      { id: '6', label: t('web.component.footer.ligen'), route: '/ligen', order: 5, isVisible: true },
      { id: '7', label: t('web.component.footer.verbaende'), route: '/verbaende', order: 6, isVisible: true },
    ],
  },
  {
    id: '2',
    title: t('web.component.footer.rechtliches'),
    order: 1,
    isVisible: true,
    items: [
      { id: '1', label: t('web.component.footer.impressum'), route: '/dartcloud/impressum', order: 0, isVisible: true },
      { id: '2', label: t('web.component.footer.datenschutz'), route: '/dartcloud/datenschutz', order: 1, isVisible: true },
      { id: '3', label: 'AGB', route: '/dartcloud/agb', order: 2, isVisible: true },
      { id: '4', label: t('web.component.cookie-consent-banner.cookie-richtlinie'), route: '/dartcloud/cookie-richtlinie', order: 3, isVisible: true },
    ],
  },
  {
    id: '3',
    title: t('web.component.footer.ueber-dartcloud'),
    order: 2,
    isVisible: true,
    items: [
      { id: '1', label: t('web.component.footer.ueber-uns'), route: '/info', order: 0, isVisible: true },
      { id: '2', label: t('web.component.footer.forum'), route: '/forum', order: 1, isVisible: true },
      { id: '3', label: t('web.component.footer.kontakt'), route: '/kontakt', order: 2, isVisible: true },
      { id: '4', label: t('web.component.footer.community'), route: '/community', order: 3, isVisible: true },
      // Guides-Bereich (07.10.2026) – Fallback; im Admin → Footer ebenfalls eintragen, wenn Sektionen aus dem Backend kommen
      { id: '5', label: t('web.component.footer.guides'), route: '/guides', order: 4, isVisible: true },
      // High-Finish-Seiten (SEO, 2026-10-12) – Fallback wie Guides
      { id: '6', label: t('web.component.footer.high-finishes'), route: '/high-finishes', order: 5, isVisible: true },
    ],
  },
  // Lösungen (2026-09-20): Feature-Landingpages – Fallback, solange im Backend
  // (Admin → Footer) keine Sektion „Lösungen“ mit diesen Routen angelegt ist.
  {
    id: '4',
    title: t('web.component.footer.loesungen'),
    order: 3,
    isVisible: true,
    items: [
      { id: '1', label: t('web.component.footer.fuer-vereine'), route: '/loesungen/dartvereine', order: 0, isVisible: true },
      { id: '2', label: t('web.component.footer.fuer-spielstaetten'), route: '/loesungen/spielstaetten', order: 1, isVisible: true },
      { id: '3', label: t('web.component.footer.turnier-anmeldung'), route: '/loesungen/turnier-anmeldung', order: 2, isVisible: true },
      { id: '4', label: t('web.component.footer.turniersoftware'), route: '/loesungen/turniersoftware', order: 3, isVisible: true },
      { id: '5', label: t('web.component.footer.alle-loesungen'), route: '/loesungen', order: 4, isVisible: true },
    ],
  },
];

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState<FooterSection[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    footerService.getVisibleSections().then((data) => {
      if (!cancelled) setSections(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const effective = (sections ?? FALLBACK_SECTIONS)
    .filter((s) => s.isVisible)
    .sort((a, b) => a.order - b.order);

  const handleLink = (route: string) => {
    if (!route) return;
    if (route.startsWith('http://') || route.startsWith('https://') || route.startsWith('mailto:')) {
      window.open(route, '_blank', 'noopener,noreferrer');
    } else {
      navigate(route);
    }
  };

  const renderColumn = (section: FooterSection) => (
    <div key={section.id} className="min-w-0">
      <h3 className="mb-4 text-xs font-semibold uppercase tracking-widest text-gray-500">
        {section.title}
      </h3>
      <ul className="space-y-3">
        {section.items
          .filter((i) => i.isVisible)
          .sort((a, b) => a.order - b.order)
          .map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => handleLink(item.route)}
                className="rounded-md text-left text-sm text-gray-400 transition-colors hover:text-[#3fced1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
              >
                {item.label}
              </button>
            </li>
          ))}
      </ul>
    </div>
  );

  const logo = (
    <button type="button" onClick={() => navigate('/')} aria-label={t('web.component.footer.zur-startseite')}>
      <img
        src="/DC_Logo_Typo_Blau_gross.png"
        alt={t('web.component.footer.dartcloud')}
        className="dc-logo-dark h-8 w-auto object-contain"
      />
      <img
        src="/DC_Logo_Typo_SchwarzBlau_gross.png"
        alt={t('web.component.footer.dartcloud')}
        className="dc-logo-light hidden h-8 w-auto object-contain"
      />
    </button>
  );

  const description = (
    <p className="text-sm leading-relaxed text-gray-400">
      {t('web.component.footer.die-suchmaschine-des-dartsports')}
      <br />
      {t('web.component.footer.finde-turniere-vereine-spielstaetten-und')}
    </p>
  );

  const social = (
    <div className="flex items-center gap-3">
      <button
        type="button"
        aria-label={t('web.component.footer.instagram')}
        onClick={() => handleLink('https://instagram.com/dartcloudapp')}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-800 text-gray-400 transition-all hover:-translate-y-0.5 hover:bg-[#3fced1] hover:text-black"
      >
        <Globe size={18} />
      </button>
      <button
        type="button"
        aria-label={t('web.component.footer.e-mail')}
        onClick={() => handleLink('mailto:info@dartcloudapp.de')}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-800 text-gray-400 transition-all hover:-translate-y-0.5 hover:bg-[#3fced1] hover:text-black"
      >
        <Mail size={18} />
      </button>
    </div>
  );

  const copyright = (
    <p className="text-xs text-gray-500">
      © {new Date().getFullYear()}{' '}{t('web.component.footer.dartcloud-alle-rechte-vorbehalten')}
    </p>
  );

  return (
    <footer className="relative bg-black">
      {/* Gradient-Border oben */}
      <div
        aria-hidden="true"
        className="h-px w-full bg-gradient-to-r from-transparent via-[#3fced1]/50 to-transparent"
      />
      <div className="mx-auto max-w-7xl px-6 py-12 sm:px-8">
        {/* Oberer Bereich */}
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[2fr_repeat(3,1fr)]">
          <div>
            {logo}
            <div className="mt-4">{description}</div>
          </div>
          {effective.map(renderColumn)}
        </div>

        {/* Darts in deiner Stadt (2026-09-20): 10 Top-Orte als Links, Daten aus dem
            geteilten usePlacesTop-Cache (kein zweiter Request neben der Startseite) */}
        <TopCitiesSection limit={10} variant="compact" className="mt-10" />

        {/* Divider */}
        <div className="my-8 h-px bg-zinc-800" />

        {/* Unterer Bereich */}
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          {copyright}
          {social}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
